import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomValidators } from '../../helper/custom.validators';
import { InviteUserDto, User, UserStatusType } from '../../model/auth/User';
import { AuthService } from '../../service/auth.service';
import { LocalStorageService } from 'ngx-webstorage';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageKeys } from '../../helper/local-storage.keys';
import { UrlHelper } from '../../helper/url.helper';
import { NotificationHandler } from '../../helper/notification.handler';
import { localizeRole, Role } from '../../model/auth/Role';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { Locale } from '../../model/Locale';
import { MobileAwareComponent } from '../../common/mobile-aware/mobile-aware.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { UserService } from '../../service/user.service';
import { EventType, TrackingService } from '../../service/tracking.service';
import { environment } from '../../../environments/environment';
import { ButtonComponent } from '../../common/button/button.component';
import { TenantService } from '../../service/tenant.service';
import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import smartlookClient from 'smartlook-client';

@Component({
  selector: 'sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss']
})
export class SignUpComponent extends MobileAwareComponent implements OnInit {

  readonly requirePhone = [Role.Teacher, Role.Coordinator];

  inProgress = false;
  userExists = false;
  userExistenceChecked: boolean = false;
  userStatus?: UserStatusType;

  invitationExpired = false;
  invitationRequestDetails = '';

  confirmUsername: string | null | undefined;
  confirmEmail: string | null | undefined;
  confirmPass: string | null | undefined;
  confirmRole: Role | null | undefined;
  confirmFirstName?: string;
  confirmLastName?: string;

  inviteRequestMode?: boolean;

  user: CognitoUser | undefined;
  readonly roles: Role[] = [];

  readonly signUpRole: Role;
  readonly locale: Locale;

  emailField = new FormControl('', [
    Validators.email, Validators.required, Validators.minLength(5), Validators.maxLength(128)
  ]);
  phoneField = new FormControl('', [
    CustomValidators.phoneNumber, Validators.minLength(7), Validators.maxLength(15)
  ]);
  firstNameField = new FormControl('', [
    Validators.required, Validators.minLength(2), Validators.maxLength(128)
  ]);
  lastNameField = new FormControl('', [
    Validators.required, Validators.minLength(2), Validators.maxLength(128)
  ]);
  roleField = new FormControl({value: '', disabled: true}, []);
  passwordField = new FormControl('', [
    CustomValidators.password
  ]);

  signUpForm: FormGroup = new FormGroup({
    email: this.emailField,
    phone: this.phoneField,
    firstName: this.firstNameField,
    lastName: this.lastNameField,
    role: this.roleField,
    password: this.passwordField
  });

  readonly supportEmail: string;
  readonly supportEmailLabel: string;

  searchUserByEmail = () => {
    this.verifyUserExists(true);
  };

  constructor(private authService: AuthService,
              private userService: UserService,
              private trackingService: TrackingService,
              private tenantService: TenantService,
              private localStorageService: LocalStorageService,
              private router: Router,
              private route: ActivatedRoute,
              breakpointObserver: BreakpointObserver,
              @Inject(LOCALE_ID) locale: string) {
    super(breakpointObserver);

    const role = this.route.snapshot.paramMap.get('role') as Role;
    const allowedRoles = new Set<Role>([Role.Student, Role.Teacher]);
    this.signUpRole = allowedRoles.has(role) ? role : Role.Student;

    this.locale = locale as Locale;

    this.supportEmailLabel = environment.contacts.supportEmail;
    this.supportEmail = 'mailto:' + this.supportEmailLabel;

    const url = this.router.url;
    if (url.startsWith(UrlHelper.INVITE_REQUEST_PATH)) {
      this.inviteRequestMode = true;
    }

    this.confirmUsername = decodeURIComponent(this.route.snapshot.queryParamMap.get('username') || '').replace(' ', '+');
    this.confirmEmail = decodeURIComponent(this.route.snapshot.queryParamMap.get('email') || '').replace(' ', '+');
    this.confirmPass = decodeURIComponent(this.route.snapshot.queryParamMap.get('pass') || '');
    this.confirmRole = this.route.snapshot.queryParamMap.get('role') as Role;
    this.confirmFirstName = decodeURIComponent(this.route.snapshot.queryParamMap.get('first_name') as string || '');
    this.confirmLastName = decodeURIComponent(this.route.snapshot.queryParamMap.get('last_name') as string || '');
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();

    if (this.phoneRequired) {
      this.phoneField.setValidators([
        Validators.required, CustomValidators.phoneNumber, Validators.minLength(7), Validators.maxLength(15)
      ]);
      this.phoneField.updateValueAndValidity();
    }

    const competition = this.tenantService.getTenant();
    if (competition) {
      this.authService.updateTenantConfig(competition.name, competition.domain);
    }

    if (this.inviteRequestMode) {
      const email = this.route.snapshot.queryParamMap.get('email');
      if (email) this.confirmEmail = email;

      if (this.inviteRequestMode && this.confirmEmail && this.route.snapshot.queryParamMap.get('code')) {
        const session = await this.authService.getSession();
        if (session.isValid()) {
          await this.signOut();
          this.tenantService.loadTenantDetails();
        }

        this.emailField.setValue(this.confirmEmail);
        this.searchUserByEmail();
      }
    }

    if (this.confirmUsername) {
      this.emailField.setValue(this.confirmEmail);
      this.emailField.disable();

      if (this.confirmFirstName) this.firstNameField.setValue(this.confirmFirstName);
      if (this.confirmLastName) this.lastNameField.setValue(this.confirmLastName);

      if (this.confirmRole) {
        this.roles.push(this.confirmRole);
        this.roleField.setValue(this.confirmRole);
      }

      const session = await this.authService.getSession();
      if (session.isValid()) {
        await this.signOut();
        this.tenantService.loadTenantDetails();

        NotificationHandler.handleWarning($localize`:Sign Up|Sign Up invitation sign out notification@@sign-up-invitation-sign-out-notification:You were signed out to accept the invitation. Please, follow the invitation URL again to finish a profile setup if needed.`);
      }

      this.loadUserDetails(this.confirmUsername, this.confirmPass || '');
    }

    const session = await this.authService.getSession();
    if (session && session.isValid()) {
      NotificationHandler.handleWarning($localize`:General|This page is not available for authorized users@@general-this-page-is-not-available-for-authorized-users-message:This page is not available for authorized users message`);
      await this.router.navigate([UrlHelper.DEFAULT_AUTH_PATH], {replaceUrl: true});
    }
  }

  async signOut() {
    try {
      await this.authService.logout();
      this.localStorageService.clear();
      Sentry.configureScope(scope => scope.setUser(null));
      if (environment.smartlook.enabled) smartlookClient.anonymize();
    } catch (e) {
      NotificationHandler.handleError(e);
    }
  }

  get phoneRequired(): boolean {
    return (!!this.signUpRole && this.requirePhone.includes(this.signUpRole))
      || (!!this.confirmRole && this.requirePhone.includes(this.confirmRole));
  }

  get allowSocialSignIn(): boolean {
    return false;
  }

  get studentSignUp() {
    return this.signUpRole == Role.Student;
  }

  get signInUrl() {
    return UrlHelper.SIGN_IN_PATH;
  }

  get privacyUrl() {
    return `/${this.locale}${UrlHelper.PRIVACY_PATH}`;
  }

  get termsUrl() {
    return `/${this.locale}${UrlHelper.TERMS_PATH}`;
  }

  shouldDisableSubmit() {
    return !this.signUpForm.valid || this.userExists;
  }

  compareRoles(r1: Role, r2: Role) {
    return r1 == r2;
  }

  displayRole(role: Role) {
    return localizeRole(role);
  }

  loadUserDetails(username: string, pass: string) {
    this.inProgress = true;

    this.authService.signIn(username, pass)
      .then(user => {
        this.user = user;
        this.inProgress = false;
      })
      .catch(error => {
        const code: string = (error as { [key: string]: string | undefined })['code'] as string;

        if (code == 'NotAuthorizedException') {
          this.invitationExpired = true;
        } else {
          NotificationHandler.handleWarning($localize`:Sign Up|User sign in with tmp credentials error message@@sign-up-user-sign-in-with-tmp-credentials-error:Sign in with temporary credentials has failed`, error);
        }

        this.inProgress = false;
      })
      .finally(() => this.userExistenceChecked = true);
  }

  signUp() {
    if (this.signUpForm.valid) {
      this.inProgress = true;

      const user = new User();
      user.email = this.emailField.value as string;
      user.phone = this.phoneField.value as string || '';
      user.firstName = this.firstNameField.value as string;
      user.lastName = this.lastNameField.value as string;

      const password = this.passwordField.value as string;

      if (this.user) {
        this.authService.completeSignUp(this.user, password, user.firstName, user.lastName, user.phone)
          .then(async () => {
            this.trackingService.sendEvent(EventType.sign_up, undefined, this.roleField.value as string);

            const u = await this.authService.getUser();
            if (u) this.userService.reportUpdateUser(u.id, true, true).subscribe({
              next: () => {
              }, error: error => {
                NotificationHandler.handleError($localize`:User Profile|User profile update report failed notification@@user-profile-report-failed-notification:Profile update report failed`, error);
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
              }, complete: async () => {
                await this.router.navigate([`${UrlHelper.DEFAULT_AUTH_PATH}`]);
              }
            });
          })
          .catch((error) => {
            if ((error as Error).message == 'Invalid session for the user, session is expired.') {
              NotificationHandler.handleWarning($localize`:Sign Up|User complete sign up session expired error message@@sign-up-complete-sign-up-session-expired-error:The temporary token has expired. Please refresh the page and try to fill in your details faster.`);
            } else {
              NotificationHandler.handleError($localize`:Sign Up|User sign up error message@@sign-up-user-error:User sign up has failed`, error);
            }
            this.inProgress = false;
          });
      } else {
        this.authService.signUp(user, password, this.signUpRole, this.locale)
          .then(async user => {
            this.localStorageService.store(LocalStorageKeys.SIGN_UP_CONFIRM_EMAIL, user.email);
            this.localStorageService.store(LocalStorageKeys.SIGN_UP_CONFIRM_PASSWORD, password);

            this.trackingService.sendEvent(EventType.sign_up, undefined, this.signUpRole);

            await this.router.navigate([`${UrlHelper.SIGN_UP_CONFIRM_PATH}${user.email}/${user.id}`]);
          })
          .catch((error) => {
            const code: string = (error as { [key: string]: string | undefined })['code'] as string;

            if (code == 'UsernameExistsException') {
              NotificationHandler.handleWarning($localize`:Sign Up|User exists sign up error message@@sign-up-user-exists-error:An account with the given email already exists`);
            } else {
              NotificationHandler.handleError($localize`:Sign Up|User sign up error message@@sign-up-user-error:User sign up has failed`, error);
            }

            this.inProgress = false;
          });
      }
    }
  }

  verifyUserExists(trackStatus = false) {
    if (this.emailField.value && this.emailField.valid) {
      if (trackStatus) {
        this.inProgress = true;
        this.userExistenceChecked = false;
      }
      this.userService.userExists(this.emailField.value as string).subscribe(user => {
        if (user.id) {
          this.emailField.disable();
          this.firstNameField.disable();
          this.lastNameField.disable();
          this.passwordField.disable();

          this.userExists = true;
          this.userStatus = user.status;
        }

        if (trackStatus) {
          this.inProgress = false;
          this.userExistenceChecked = true;
        }
      });
    }
  }

  requestInvitation(requestInvitationBtn: ButtonComponent) {
    requestInvitationBtn.disabled = true;

    if (this.inviteRequestMode && this.userExistenceChecked && this.userExists) {
      this.confirmEmail = this.emailField.value as string;
    }

    if (this.confirmEmail && (this.confirmRole || this.inviteRequestMode)) {
      this.inProgress = true;

      const competition = this.tenantService.getTenant();

      const data: InviteUserDto = {
        email: this.confirmEmail,
        role: this.confirmRole || Role.Student,
        resend: true,
        resend_tenant_name: competition?.name || '',
        resend_tenant_domain: competition?.domain || '',
        resend_locale: this.locale,
      };
      this.userService.inviteUserResend(data).subscribe({
        next: () => {
          this.invitationRequestDetails = $localize`:User Profile|User profile invitation requested notification@@user-profile-invitation-requested-notification:New invitation was requested and should arrive in your inbox soon`;
        }, error: error => {
          if (error instanceof HttpErrorResponse) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (error.error && error.error.code == 'Conflict') {
              NotificationHandler.handleWarning($localize`:Notifications|User don't need invitation message message@@notifications-user-do-not-need-invitation-message:This user profile cannot get an invitation anymore. Most likely, an invitation was already accepted before, and the user does not need one anymore.`);
            } else {
              NotificationHandler.handleError($localize`:Notifications|Request user invitation has failed message@@notifications-request-user-invitation-failed-error:Request user invitation has failed`, error);
            }
          } else {
            NotificationHandler.handleError($localize`:Notifications|Request user invitation has failed message@@notifications-request-user-invitation-failed-error:Request user invitation has failed`, error);
          }
        }, complete: () => {
          this.inProgress = false;
        }
      });
    } else {
      NotificationHandler.handleError($localize`:Notifications|Request user invitation has failed message@@notifications-request-user-invitation-failed-error:Request user invitation has failed`);
    }
  }
}
